import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OrganizationLogoUpload } from "@/features/Organization/components/OrganizationLogoUpload";
import { InputWithMagicFill } from "@/shared/components/form/InputWithMagicFill";
import { TextareaWithMagicFill } from "@/shared/components/form/TextareaWithMagicFill";

interface OrganizationFormProps {
  orgData: {
    name: string;
    website: string;
    info: string;
    logo_url: string | null;
  };
  isSubmitting: boolean;
  onChange: (field: string, value: string | null, file?: File) => void;
  onSubmit: (e: React.FormEvent) => void;
  organizationId?: string;
}

export const OrganizationForm = ({
  orgData,
  isSubmitting,
  onChange,
  onSubmit,
  organizationId,
}: OrganizationFormProps) => {
  const { t } = useTranslation();
  const [_isMagicFillLoading, setIsMagicFillLoading] = useState(false);

  const handleLogoUpdate = (url: string, file?: File) => {
    onChange("logo_url", url, file);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <OrganizationLogoUpload
        organizationId={organizationId}
        currentLogoUrl={orgData.logo_url}
        onLogoUpdate={handleLogoUpdate}
      />
      <div className="space-y-2">
        <label className="text-sm font-medium">
          {t("settings.organization.name")}
        </label>
        <InputWithMagicFill
          value={orgData.name}
          onChange={(e) => onChange("name", e.target.value)}
          placeholder={t("settings.organization.namePlaceholder")}
          formType="organization_admin"
          fieldKey="name"
          getCurrentValues={() => orgData}
          metadata={{ organizationId }}
          onMagicFillApply={(value) => onChange("name", value)}
          onLoadingChange={setIsMagicFillLoading}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">
          {t("settings.organization.website")}
        </label>
        <InputWithMagicFill
          value={orgData.website}
          onChange={(e) => onChange("website", e.target.value)}
          placeholder={t("settings.organization.websitePlaceholder")}
          formType="organization_admin"
          fieldKey="website"
          getCurrentValues={() => orgData}
          metadata={{ organizationId }}
          onMagicFillApply={(value) => onChange("website", value)}
          onLoadingChange={setIsMagicFillLoading}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">
          {t("settings.organization.info")}
        </label>
        <TextareaWithMagicFill
          value={orgData.info}
          onChange={(e) => onChange("info", e.target.value)}
          placeholder={t("settings.organization.infoPlaceholder")}
          formType="organization_admin"
          fieldKey="info"
          getCurrentValues={() => orgData}
          metadata={{ organizationId }}
          onMagicFillApply={(value) => onChange("info", value)}
          onLoadingChange={setIsMagicFillLoading}
          className="min-h-[160px]"
        />
      </div>
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full sm:w-auto"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("common.actions.saving")}
          </>
        ) : (
          t("common.actions.save")
        )}
      </Button>
    </form>
  );
};
